import NotificationSetting, { NotificationSettingType } from "@/types/NotificationSetting.interface"
import TenantMembership from "@/types/TenantMembership.interface"

export interface NotificationSettingPayload {
    id: number,
    type: NotificationSettingType
}

export interface ResellerPayload {
    user_id: number
}

interface State {
    currentMembership: TenantMembership
    tenantMemberships: TenantMembership[]
    browserNotifications: NotificationSetting[]
    emailNotifications: NotificationSetting[]
    pushNotifications: NotificationSetting[]
}

const defaults = () => {
    return {
        currentMembership: null,
        browserNotifications: null,
        emailNotifications: null,
        pushNotifications: null,
        tenantMemberships: [],
    }
}

export default {
    state: defaults(),
    mutations: {
        currentMembershipSet (state: State, membership: TenantMembership): void {
            state.currentMembership = membership
        },
        tenantMembershipsPush (state: State, memberships: TenantMembership[]): void {
            //  Update our tenant memberships
            state.tenantMemberships = window.removeDuplicatesById(state.tenantMemberships, memberships)

            //  Check if we have a current membership
            const currentMembership = state.currentMembership

            //  If no current, do nothing
            if (!currentMembership) return

            //  Else, check if it was updated
            const updatedMembership = memberships.find((x: TenantMembership) => x.id === currentMembership.id)

            //  If it was not, do nothing
            if (!updatedMembership) return

            //  Else, replace our current membership
            state.currentMembership = updatedMembership
        },
        tenantMembershipsSet (state: State, memberships: TenantMembership[]): void {
            state.tenantMemberships = memberships
        },
        tenantMembershipBrowserNotificationsSet (state: State, obj: NotificationSetting[]): void {
            //  Set our notifications based on type
            state.browserNotifications = obj
        },
        tenantMembershipEmailNotificationsSet (state: State, obj: NotificationSetting[]): void {
            //  Set our notifications based on type
            state.emailNotifications = obj
        },
        tenantMembershipPushNotificationsSet (state: State, obj: NotificationSetting[]): void {
            //  Set our notifications based on type
            state.pushNotifications = obj
        },
        membershipsReset (state: State) {
            Object.assign(state, defaults())
        },
    },
    actions: {
        currentMembershipSet ({ commit, dispatch }, membership: TenantMembership): void {
            //  Set our current membership
            commit('currentMembershipSet', membership)

            //  Our payload
            const payload = {id: membership.id}

            //  Fetch our notifications
            dispatch('tenantMembershipsNotificationsGet', {...payload, type: 'browser'})
            dispatch('tenantMembershipsNotificationsGet', {...payload, type: 'email'})
            dispatch('tenantMembershipsNotificationsGet', {...payload, type: 'push'})
        },
        tenantMembershipsGet ({ commit }): Promise<TenantMembership[]> {
            //  Assemble the API endpoint
            const url = `/memberships`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Fill our tenant memberships array
                commit('tenantMembershipsSet', response.data)

                //  Return the memberships
                return response.data
            }).catch(() => null)
        },
        tenantMembershipsNotificationsGet ({ commit, getters }, params: NotificationSettingPayload): Promise<void> {
            //  Assemble the API endpoint
            const url = `${getters.api}/memberships/notifications?${window.toUrlParams(params)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Grab our type based on the notification type
                const type = params.type

                //  Get our type of notifications
                const method = {
                    browser: 'tenantMembershipBrowserNotificationsSet',
                    email: 'tenantMembershipEmailNotificationsSet',
                    push: 'tenantMembershipPushNotificationsSet'
                }[type]

                //  Commit our notification settings
                commit(method, response.data)
            }).catch(() => null)
        },
        tenantMembershipsPut ({ commit, getters }, membership: TenantMembership): Promise<void> {
            //  Assemble the API endpoint
            const url = `${getters.api}/memberships`

            //  Send the request
            return window.axios.put(url, membership).then(response => {
                //  Update our memberships
                commit('tenantMembershipsPush', [response.data])

                //  Return the updated membership
                return response.data
            }).catch(() => null)
        }
    },
    getters: {
        currentMembership (state: State): TenantMembership {
            return state.currentMembership
        },
        browserNotifications (state: State): NotificationSetting[] {
            return state.browserNotifications
        },
        emailNotifications (state: State): NotificationSetting[] {
            return state.emailNotifications
        },
        pushNotifications (state: State): NotificationSetting[] {
            return state.pushNotifications
        },
        tenantMemberships (state: State): TenantMembership[] {
            return state.tenantMemberships
        }
    }
}
